/*
 * Headless customer portal — /pages/manage-subscription (2026-07-29).
 *
 * Replaces Recharge's hosted Affinity portal as the customer's subscription
 * hub. Everything renders and mutates HERE, on our domain, through the App
 * Proxy → formulator → Recharge Admin API:
 *
 *   GET  /account/subscriptions                 list (id, status, formula)
 *   GET  /account/subscriptions/:id/overview    live sub + stack + address
 *   POST /formula/submit                        mint variant for edited stack
 *   POST /account/subscriptions/:id/swap        re-point sub (price follows)
 *   POST /account/subscriptions/:id/skip        next delivery +1 interval
 *   POST /account/subscriptions/:id/next-date   exact next delivery date
 *   POST /account/subscriptions/:id/address     delivery address update
 *   POST /account/subscriptions/:id/cancel      self-serve cancel
 *
 * The ONE thing that stays on Recharge is card entry (PCI): the payment
 * button opens /tools/recurring/portal/<hash> (overview supplies the path).
 *
 * The stack editor here is a LIGHT edit surface: dose steppers stay inside
 * min_dose_mg..max_dose_mg (the soft ceiling — none of BYO's over-max
 * acknowledgement dance). Heavy re-engineering routes to the full builder
 * via BYO portal-mode, which prefills and swaps the same way. The blend name
 * is editable inline (15-char cap — same engraving constraint as BYO).
 *
 * Delivery moves: Skip (one interval), Delay (1w / 2w / 1m popup), or Pick
 * an exact date — the last two both land on POST next-date. Cancel opens a
 * soft retention popup (offer the same delays) before the real cancel step.
 *
 * ⚠ PRICING LOCKSTEP (5th copy): computeMonthlyPrice() must match
 * formulator pricing.ts / catalogue get.ts / yt-byo.js / admin lib —
 * sub = max(floor, (M + £0.50×n + £11) ÷ 0.37) — with runtime overrides
 * adopted from the catalogue payload's `pricing` block exactly like BYO.
 * The price shown while editing is a live PREVIEW; the AUTHORITATIVE price
 * comes back from the swap response and is what the success banner shows.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-yt-account-subs]');
  if (!root) return;

  var PROXY_BASE = '/apps/yutrients';
  var PORTAL_URL = root.getAttribute('data-portal-url') || '/account';
  var BUILD_URL = root.getAttribute('data-build-url') || '/pages/build-your-own';

  // Pricing-model defaults — overridden from the catalogue payload (see
  // adoptPricing), same mechanism as yt-byo.js so admin Pricing-tab changes
  // apply here too.
  var VARIANT_FLOOR = 50;
  var PRICE_BASE = 11 / 0.37;
  var PRICE_TAPER_AFTER = 15;
  var PRICE_TAPER_GBP = 0;

  var state = {
    subId: null,
    subs: [], // active/paused list — a picker renders when there's more than one
    overview: null,
    catalogue: null,
    catalogueById: null,
    searchIndex: null,
    stack: [], // working copy of the ADD-ONS [{ingredient_id, amount_mg, name, unit}]
    // The base under the blend — ONE line, sold at its own price (Lucas, 24
    // Sep 2026: "they see the base as a single line … they can select a
    // single base and then add on ingredients, not a breakdown of all the
    // ingredients inside the base"). null for a scratch blend.
    base: null, // {slug, name, sub_price_gbp, n_ingredients}
    bases: null, // the live bases from /bases, loaded when the chooser opens
    stackName: null,
    dirty: false,
    busy: false,
  };

  // --- tiny DOM + format helpers ------------------------------------------

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function btn(cls, text, onClick) {
    var b = el('button', cls, text);
    b.type = 'button';
    b.addEventListener('click', onClick);
    return b;
  }
  function fmtDate(iso) {
    if (!iso) return null;
    var d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  /** Delivery-facing dates (founder 2026-08-11): the portal speaks ARRIVAL
   *  everywhere; billing is the quiet sub-line. Arrival = charge date + 5..7
   *  days (the delivery SLA). The picker back-computes charge = arrival − 6
   *  (clamped to tomorrow) so parcels aim a day early, never late; the
   *  earliest pickable arrival is 5 days out (founder rule). */
  var DELIVERY_MIN_DAYS = 5;
  var DELIVERY_MAX_DAYS = 7;
  var ARRIVAL_BACKOFF_DAYS = 6;
  function dateFromIso(iso) {
    if (!iso) return null;
    var d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }
  function addDays(d, n) { var c = new Date(d.getTime()); c.setDate(c.getDate() + n); return c; }
  function isoOf(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  function fmtDayMonth(d, style) {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: style || 'long' });
  }
  /** "15 – 17 August" (month names on both sides across a month edge). */
  function arrivalRange(chargeIso, monthStyle) {
    var base = dateFromIso(chargeIso);
    if (!base) return null;
    var a = addDays(base, DELIVERY_MIN_DAYS);
    var b = addDays(base, DELIVERY_MAX_DAYS);
    var left = a.getMonth() === b.getMonth()
      ? a.toLocaleDateString('en-GB', { day: 'numeric' })
      : fmtDayMonth(a, monthStyle);
    return left + ' – ' + fmtDayMonth(b, monthStyle);
  }
  /** Charge date for a picked ARRIVAL date: arrival − backoff, never before tomorrow. */
  function chargeForArrival(pickedIso) {
    var picked = dateFromIso(pickedIso);
    if (!picked) return null;
    var charge = addDays(picked, -ARRIVAL_BACKOFF_DAYS);
    var tomorrow = addDays(new Date(), 1);
    tomorrow.setHours(0, 0, 0, 0);
    if (charge.getTime() < tomorrow.getTime()) charge = tomorrow;
    return isoOf(charge);
  }
  function fmtMoney(v) {
    if (v == null || v === '') return null; // Number(null) is 0 — don't render a phantom £0.00
    var n = Number(v);
    return isNaN(n) ? null : '£' + n.toFixed(2);
  }
  // Doses display in the ingredient's listed (legal selling) unit, exactly
  // like BYO: canonical mg × unitFactor, no auto-scaling, µg symbol proper.
  function unitFactor(unit) {
    var u = String(unit || 'mg').toLowerCase();
    if (u === 'mcg' || u === 'ug') return 1000;
    if (u === 'g') return 0.001;
    return 1;
  }
  function unitLabel(unit) {
    var u = String(unit || 'mg').toLowerCase();
    if (u === 'mcg' || u === 'ug') return 'µg';
    // bn_cfu (billion CFU, probiotics) — a word, so it gets a space below.
    if (u === 'bn_cfu' || u === 'bncfu') return ' bn CFU';
    return String(unit || 'mg');
  }
  function mgToDisplay(mg, unit) {
    var v = Number(mg) * unitFactor(unit);
    if (!isFinite(v)) return '0' + unitLabel(unit);
    var r = Math.round(v * 1e6) / 1e6;
    return String(r) + unitLabel(unit);
  }
  function round2(n) { return Math.round(n * 100) / 100; }
  function track(name, params) {
    try { if (window.ytTrack) window.ytTrack(name, params || {}); } catch (e) { /* never break the portal */ }
  }
  function newIdempotencyKey() {
    try { return window.crypto.randomUUID(); } catch (e) {
      return 'idem-' + Date.now() + '-' + Math.random().toString(16).slice(2);
    }
  }

  function api(path, opts) {
    opts = opts || {};
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, opts.timeout || 25000);
    return fetch(PROXY_BASE + path, {
      method: opts.method || 'GET',
      headers: Object.assign(
        { Accept: 'application/json' },
        opts.body ? { 'Content-Type': 'application/json' } : {},
        opts.headers || {}
      ),
      credentials: 'same-origin',
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: controller.signal,
    }).then(function (res) {
      clearTimeout(timer);
      return res.json().catch(function () { return {}; }).then(function (j) {
        j.__status = res.status;
        return j;
      });
    }, function (err) {
      clearTimeout(timer);
      throw err;
    });
  }

  // The formulator answers two ways: the account routes send a flat
  // { ok, error, message }; /formula/submit throws its error envelope,
  // { error: { code, message, details: { message_for_human } } }. This page
  // only ever read the flat shape, so a refused save showed the generic
  // "We could not save your blend" instead of the server's "Magnesium
  // Glycinate (we go up to 400mg a day)" — Robin Maximova, 28 Sep 2026,
  // five refusals in a row and not one explained.
  function apiMessage(j, fallback) {
    if (!j) return fallback;
    if (typeof j.message === 'string' && j.message) return j.message;
    var e = j.error;
    if (e && typeof e === 'object') {
      if (e.details && typeof e.details.message_for_human === 'string' && e.details.message_for_human) {
        return e.details.message_for_human;
      }
      if (typeof e.message === 'string' && e.message) return e.message;
    }
    return fallback;
  }

  // --- ingredient search (ported verbatim from yt-byo.js) ------------------
  //
  // Ranked, typo-tolerant lookup — one engine across BYO / admin / here, so
  // "gingar" finds Ginger in the portal too. Tiers per query token, strongest
  // first: exact field → field prefix → word prefix → substring → one-typo
  // word (bounded Damerau-Levenshtein) → in-order subsequence. Fields are
  // weighted name > alias > tagline > category > tags; multi-word queries AND.

  function searchNormalise(s) {
    return String(s || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function searchField(raw, weight) {
    var text = searchNormalise(raw);
    if (!text) return null;
    return { text: text, words: text.split(' '), weight: weight };
  }

  function buildSearchIndex(items) {
    return items.map(function (it) {
      var fields = [];
      var push = function (f) { if (f) fields.push(f); };
      push(searchField(it.name, 100));
      (it.aliases || []).forEach(function (a) { push(searchField(a, 85)); });
      push(searchField(it.tagline, 55));
      push(searchField(it.category, 40));
      (it.discovery_tags || []).forEach(function (t) { push(searchField(t, 35)); });
      (it.goal_tags || []).forEach(function (t) { push(searchField(t, 35)); });
      return { id: it.id, fields: fields };
    });
  }

  /** Bounded Damerau-Levenshtein: true when distance ≤ maxEdits. */
  function withinEdits(a, b, maxEdits) {
    if (maxEdits <= 0) return a === b;
    if (Math.abs(a.length - b.length) > maxEdits) return false;
    var m = a.length;
    var n = b.length;
    var prevPrev = [];
    var prev = [];
    for (var j = 0; j <= n; j++) prev.push(j);
    for (var i = 1; i <= m; i++) {
      var cur = [i];
      var rowMin = i;
      for (var k = 1; k <= n; k++) {
        var cost = a.charAt(i - 1) === b.charAt(k - 1) ? 0 : 1;
        var v = Math.min(prev[k] + 1, cur[k - 1] + 1, prev[k - 1] + cost);
        if (
          i > 1 && k > 1 &&
          a.charAt(i - 1) === b.charAt(k - 2) &&
          a.charAt(i - 2) === b.charAt(k - 1)
        ) {
          v = Math.min(v, prevPrev[k - 2] + 1); // transposition
        }
        cur.push(v);
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > maxEdits) return false; // whole row over budget → bail
      prevPrev = prev;
      prev = cur;
    }
    return prev[n] <= maxEdits;
  }

  function typoBudget(token) {
    if (token.length >= 8) return 2;
    if (token.length >= 4) return 1;
    return 0;
  }

  /** In-order subsequence with an adjacency bonus; 0 when not a subsequence. */
  function subsequenceScore(token, text) {
    var ti = 0;
    var adjacent = 0;
    var last = -2;
    for (var i = 0; i < text.length && ti < token.length; i++) {
      if (text[i] === token[ti]) {
        if (i === last + 1) adjacent++;
        last = i;
        ti++;
      }
    }
    if (ti < token.length) return 0;
    return 1 + adjacent / Math.max(1, token.length - 1);
  }

  /** Tier score of one token against one field (0 = no match). */
  function tokenFieldScore(token, f) {
    if (f.text === token) return 10;
    if (f.text.indexOf(token) === 0) return 6;
    for (var i = 0; i < f.words.length; i++) if (f.words[i].indexOf(token) === 0) return 4.5;
    if (f.text.indexOf(token) !== -1) return 3;
    var budget = typoBudget(token);
    if (budget > 0) {
      for (var j = 0; j < f.words.length; j++) if (withinEdits(token, f.words[j], budget)) return 2.5;
    }
    // Subsequence only for 3+ chars — shorter tokens match everything.
    if (token.length >= 3) {
      var s = subsequenceScore(token, f.text);
      if (s > 0) return Math.min(2, s);
    }
    return 0;
  }

  /** → Map(ingredient id → score). Every token must match somewhere (AND). */
  function searchScores(index, query) {
    var tokens = searchNormalise(query).split(' ').filter(Boolean);
    var out = new Map();
    if (!tokens.length) return out;
    index.forEach(function (entry) {
      var total = 0;
      var ok = true;
      for (var t = 0; t < tokens.length; t++) {
        var best = 0;
        for (var f = 0; f < entry.fields.length; f++) {
          var tier = tokenFieldScore(tokens[t], entry.fields[f]);
          if (tier <= 0) continue;
          var s = tier * entry.fields[f].weight;
          if (s > best) best = s;
        }
        if (best <= 0) { ok = false; break; }
        total += best;
      }
      if (ok) out.set(entry.id, total);
    });
    return out;
  }

  // --- pricing (lockstep copy #5 — see header warning) --------------------

  function adoptPricing(payload) {
    var p = payload && payload.pricing;
    if (!p) return;
    if (isFinite(Number(p.variant_floor))) VARIANT_FLOOR = Number(p.variant_floor);
    if (isFinite(Number(p.base))) PRICE_BASE = Number(p.base);
    if (isFinite(Number(p.taper_after))) PRICE_TAPER_AFTER = Number(p.taper_after);
    if (isFinite(Number(p.taper_gbp))) PRICE_TAPER_GBP = Number(p.taper_gbp);
  }

  function computeMonthlyPrice() {
    if (!state.catalogueById) return null;
    // With a base: the base's own subscription price plus each add-on at
    // its component price — no fee, no floor, no taper (the base price
    // swallows the fee, exactly as the PDP and the builder price it).
    // Without one: the fee, the add-ons, the taper and the floor, as before.
    var total = state.base ? Number(state.base.sub_price_gbp) || 0 : PRICE_BASE;
    var priced = 0;
    state.stack.forEach(function (s) {
      var cat = state.catalogueById.get(s.ingredient_id);
      if (!cat || cat.raw_cost_per_mg == null) return;
      total += Number(cat.price_fee || 0) +
        Number(cat.price_multiple || 0) * (Number(cat.raw_cost_per_mg) * s.amount_mg);
      priced += 1;
    });
    if (state.base) return round2(total);
    total -= PRICE_TAPER_GBP * Math.max(0, priced - PRICE_TAPER_AFTER);
    return round2(Math.max(VARIANT_FLOOR, total));
  }

  /** The live bases (name, slug, prices, ingredient count) for the chooser. */
  function loadBases() {
    if (state.bases) return Promise.resolve(state.bases);
    return api('/bases').then(function (payload) {
      state.bases = (payload && payload.bases) || [];
      return state.bases;
    });
  }

  function loadCatalogue() {
    if (state.catalogue) return Promise.resolve(state.catalogue);
    return api('/catalogue').then(function (payload) {
      adoptPricing(payload);
      state.catalogue = (payload.ingredients || []).filter(function (i) { return i.raw_cost_per_mg != null; });
      // The add-ingredient modal browses this list directly — keep it A→Z.
      state.catalogue.sort(function (a, b) { return String(a.name).localeCompare(String(b.name)); });
      state.catalogueById = new Map(state.catalogue.map(function (i) { return [i.id, i]; }));
      state.searchIndex = buildSearchIndex(state.catalogue);
      return state.catalogue;
    });
  }

  // --- shared render bits --------------------------------------------------

  function clear() { while (root.firstChild) root.removeChild(root.firstChild); }

  function showState(title, text, actions) {
    clear();
    var box = el('div', 'yt-account__state');
    box.appendChild(el('h2', 'yt-account__state-title', title));
    if (text) box.appendChild(el('p', 'yt-account__state-text', text));
    (actions || []).forEach(function (a) { box.appendChild(a); });
    root.appendChild(box);
  }

  function showLoading(msg) {
    clear();
    var box = el('div', 'yt-account__state yt-account__loading');
    box.appendChild(el('span', 'yt-account__spinner'));
    box.appendChild(el('p', null, msg || 'Loading your subscription…'));
    root.appendChild(box);
  }

  function banner(kind, text) {
    var existing = root.querySelector('.yt-account__banner');
    if (existing) existing.remove();
    var b = el('div', 'yt-account__banner yt-account__banner--' + kind, text);
    root.insertBefore(b, root.firstChild);
    // Mobile (founder 2026-08-10): every action jumps the customer to the
    // TOP of the page so the outcome banner is unmissable — 'nearest' only
    // nudged the banner's edge into view. Desktop keeps the gentle reveal.
    try {
      if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        b.scrollIntoView({ block: 'nearest' });
      }
    } catch (e) { /* older browsers */ }
    if (kind === 'ok') setTimeout(function () { b.remove(); }, 12000);
  }

  function card(title) {
    var c = el('section', 'yt-portal-card');
    if (title) c.appendChild(el('h2', 'yt-portal-card__title', title));
    return c;
  }

  /** Small centred popup (delay options / date picker / cancel flow). */
  function popup(title) {
    var overlay = el('div', 'yt-portal-modal');
    var box = el('div', 'yt-portal-modal__box yt-portal-modal__box--pop');
    var head = el('div', 'yt-portal-modal__head');
    head.appendChild(el('h3', 'yt-portal-modal__title', title));
    head.appendChild(btn('yt-portal-modal__close', '✕', function () { overlay.remove(); }));
    box.appendChild(head);
    overlay.appendChild(box);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) overlay.remove(); });
    document.body.appendChild(overlay);
    return { overlay: overlay, box: box };
  }

  /** Delay target: scheduled next delivery + offset, based from tomorrow when
   *  the scheduled date is missing or already behind us. Returns YYYY-MM-DD. */
  function delayTargetDate(days, months) {
    var iso = state.overview && state.overview.subscription.next_charge_at;
    var base = iso ? new Date(String(iso).slice(0, 10) + 'T00:00:00Z') : null;
    var tomorrow = new Date(Date.now() + 86400000);
    if (!base || isNaN(base.getTime()) || base.getTime() < tomorrow.getTime()) base = tomorrow;
    if (months) base.setUTCMonth(base.getUTCMonth() + months);
    if (days) base.setUTCDate(base.getUTCDate() + days);
    return base.toISOString().slice(0, 10);
  }

  function postNextDate(date, verb) {
    showLoading('Moving your delivery…');
    return api('/account/subscriptions/' + encodeURIComponent(state.subId) + '/next-date', {
      method: 'POST',
      body: { date: date },
    }).then(function (r) {
      if (!r.ok) throw new Error(r.message || 'Could not move the delivery.');
      track('portal_date_changed', { verb: verb });
      return refresh().then(function () {
        var eff = r.next_charge_at || date;
        banner('ok', 'Delivery ' + verb + ' — now arriving ' + (arrivalRange(eff) || fmtDate(eff)) + ', billed ' + fmtDate(eff) + '.');
      });
    }).catch(function (err) {
      refresh().then(function () { banner('err', (err && err.message) || 'Could not move the delivery.'); });
    });
  }

  /** The three delay options (1w / 2w / 1m) as popup rows. Used by both the
   *  Delay-delivery popup and the cancel retention step. */
  function appendDelayOptions(container, closeOverlay) {
    var opts = el('div', 'yt-portal-pop__options');
    [['1 week', 7, 0], ['2 weeks', 14, 0], ['1 month', 0, 1]].forEach(function (o) {
      var target = delayTargetDate(o[1], o[2]);
      var b = btn('yt-portal-pop__option', '', function () {
        closeOverlay();
        postNextDate(target, 'delayed');
      });
      b.appendChild(el('span', 'yt-portal-pop__option-label', o[0]));
      b.appendChild(el('span', 'yt-portal-pop__option-date', 'arrives ' + (arrivalRange(target, 'short') || fmtDate(target))));
      opts.appendChild(b);
    });
    container.appendChild(opts);
  }

  // --- the portal ----------------------------------------------------------

  /** Multi-subscription chooser — one card per active/paused sub. */
  function renderPicker() {
    clear();
    var wrap = el('div', 'yt-portal-pick');
    state.subs.forEach(function (s) {
      var row = el('section', 'yt-portal-card yt-portal-pick__card');
      var info = el('div', 'yt-portal-pick__info');
      var nameRow = el('div', 'yt-portal-head__row');
      nameRow.appendChild(el('h2', 'yt-portal-pick__name', s.stack_name || 'Your blend'));
      nameRow.appendChild(el('span', 'yt-account-card__status is-' + s.status, s.status));
      info.appendChild(nameRow);
      if (s.next_charge_at) {
        info.appendChild(el('p', 'yt-portal-pick__next', 'Next delivery: arriving ' + (arrivalRange(s.next_charge_at, 'short') || fmtDate(s.next_charge_at))));
      }
      row.appendChild(info);
      row.appendChild(btn('yt-account__btn yt-account__btn--primary yt-portal-pick__btn', 'Manage', function () {
        state.subId = s.id;
        showLoading();
        refresh().catch(function () {
          showState('Something went wrong', 'We could not load that subscription. Please try again.', [
            btn('yt-account__btn yt-account__btn--primary', 'Retry', boot),
          ]);
        });
      }));
      wrap.appendChild(row);
    });
    root.appendChild(wrap);
  }

  function renderPortal() {
    var o = state.overview;
    clear();

    if (state.subs.length > 1) {
      root.appendChild(btn('yt-portal-back', '← All subscriptions', boot));
    }

    // ── header card ─────────────────────────────────────────────────────
    // Affinity-style summary strip: blend identity left, the two numbers a
    // subscriber actually cares about (price, next delivery) right. The
    // name is display-only: customers no longer name their tin (founder,
    // 28 Sep 2026) — the server sets it on every save.
    var head = el('div', 'yt-portal-head');
    var headLeft = el('div', 'yt-portal-head__left');
    var nameRow = el('div', 'yt-portal-head__row');
    var nameEl = el('h2', 'yt-portal-head__name', state.stackName || 'Your blend');
    nameRow.appendChild(nameEl);
    nameRow.appendChild(el('span', 'yt-account-card__status is-' + o.subscription.status, o.subscription.status));
    headLeft.appendChild(nameRow);
    headLeft.appendChild(el('p', 'yt-portal-head__sub', 'Monthly subscription'));
    head.appendChild(headLeft);
    var headRight = el('div', 'yt-portal-head__right');
    // Recharge can return price:null on SCI stores — fall back to the
    // formula's stored monthly price rather than rendering nothing.
    var livePrice = fmtMoney(o.subscription.price) || fmtMoney(o.formula.monthly_sub_price);
    if (livePrice) {
      var pWrap = el('div', 'yt-portal-head__stat');
      pWrap.appendChild(el('span', 'yt-portal-head__stat-label', 'Price'));
      pWrap.appendChild(el('span', 'yt-portal-head__stat-value', livePrice + '/mo'));
      headRight.appendChild(pWrap);
    }
    if (o.subscription.next_charge_at) {
      var dWrap = el('div', 'yt-portal-head__stat');
      dWrap.appendChild(el('span', 'yt-portal-head__stat-label', 'Next delivery'));
      dWrap.appendChild(el('span', 'yt-portal-head__stat-value', 'Arriving ' + (arrivalRange(o.subscription.next_charge_at, 'short') || fmtDate(o.subscription.next_charge_at))));
      headRight.appendChild(dWrap);
    }
    head.appendChild(headRight);
    root.appendChild(head);

    // Two-column desktop grid: the stack (the thing they came to edit) gets
    // the wide column; delivery / address / payment / cancel in the side rail.
    var grid = el('div', 'yt-portal-grid');
    var colMain = el('div', 'yt-portal-grid__main');
    var colSide = el('div', 'yt-portal-grid__side');
    grid.appendChild(colMain);
    grid.appendChild(colSide);

    if (o.subscription.status === 'cancelled' || o.subscription.status === 'expired') {
      var box = el('div', 'yt-account__state');
      box.appendChild(el('h2', 'yt-account__state-title', 'This subscription is cancelled'));
      box.appendChild(el('p', 'yt-account__state-text',
        'Your blend is saved — restart it any time, or build something new.'));
      var cActs = el('div', 'yt-portal-pop__actions yt-portal-paused__actions');
      if (o.subscription.status === 'cancelled') {
        cActs.appendChild(btn('yt-account__btn yt-account__btn--primary', 'Restart subscription', openResumePopup));
      }
      var again = el('a', 'yt-account__btn yt-account__btn--secondary', 'Build a new blend');
      again.href = BUILD_URL;
      cActs.appendChild(again);
      box.appendChild(cActs);
      root.appendChild(box);
      return;
    }

    // Paused: everything Recharge-side is stopped, so no stack editor or
    // delivery controls — just the two ways out.
    if (o.subscription.status === 'paused') {
      var pbox = el('div', 'yt-account__state');
      pbox.appendChild(el('h2', 'yt-account__state-title', 'Your subscription is paused'));
      pbox.appendChild(el('p', 'yt-account__state-text',
        'No deliveries or payments while paused — your blend stays saved. Resume whenever you like.'));
      var pActs = el('div', 'yt-portal-pop__actions yt-portal-paused__actions');
      pActs.appendChild(btn('yt-account__btn yt-account__btn--primary', 'Resume subscription', openResumePopup));
      pActs.appendChild(btn('yt-account__btn yt-portal-pop__danger-btn', 'Cancel subscription', function () {
        cancelReasonStep(popup('Cancel your subscription?'));
      }));
      pbox.appendChild(pActs);
      root.appendChild(pbox);
      return;
    }

    /** Resume: the customer picks when the next delivery lands (always an
     *  explicit future date — never an instant surprise charge). */
    function openResumePopup() {
      var p = popup('Resume your subscription');
      p.box.appendChild(el('p', 'yt-portal-pop__text',
        'Welcome back! When would you like your next delivery? Your monthly schedule continues from that date.'));
      function resumeTarget(days, months) {
        var d = new Date();
        if (months) d.setUTCMonth(d.getUTCMonth() + months);
        if (days) d.setUTCDate(d.getUTCDate() + days);
        var iso = d.toISOString().slice(0, 10);
        var tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
        return iso < tomorrow ? tomorrow : iso;
      }
      var opts = el('div', 'yt-portal-pop__options');
      [['As soon as possible', 1, 0], ['In 1 week', 7, 0], ['In 2 weeks', 14, 0], ['In 1 month', 0, 1]]
        .forEach(function (oDef) {
          var target = resumeTarget(oDef[1], oDef[2]);
          var b = btn('yt-portal-pop__option', '', function () {
            p.overlay.remove();
            showLoading('Resuming…');
            api('/account/subscriptions/' + encodeURIComponent(state.subId) + '/resume', {
              method: 'POST',
              body: { date: target },
            }).then(function (r) {
              if (!r.ok) throw new Error(r.message || 'Could not resume just now.');
              track('portal_resumed', {});
              return refresh().then(function () {
                banner('ok', 'Welcome back — your next delivery is ' + (fmtDate(r.next_charge_at) || fmtDate(target)) + '.');
              });
            }).catch(function (err) {
              refresh().then(function () { banner('err', (err && err.message) || 'Could not resume just now.'); });
            });
          });
          b.appendChild(el('span', 'yt-portal-pop__option-label', oDef[0]));
          b.appendChild(el('span', 'yt-portal-pop__option-date', 'arrives ' + (arrivalRange(target, 'short') || fmtDate(target))));
          opts.appendChild(b);
        });
      p.box.appendChild(opts);
    }

    // ── stack card ───────────────────────────────────────────────────────
    var stackCard = card('Your stack');
    var list = el('div', 'yt-portal-stack');
    stackCard.appendChild(list);

    var footer = el('div', 'yt-portal-stack__footer');
    footer.appendChild(btn('yt-account__btn yt-account__btn--secondary', '+ Add ingredient', openAddModal));
    var priceEl = el('p', 'yt-portal-stack__price');
    footer.appendChild(priceEl);
    stackCard.appendChild(footer);

    var saveRow = el('div', 'yt-portal-save');
    var saveBtn = btn('yt-account__btn yt-account__btn--primary yt-portal-save__btn', 'UPDATE MY SUBSCRIPTION', saveStack);
    saveBtn.disabled = true;
    saveRow.appendChild(saveBtn);
    var saveNote = el('p', 'yt-portal-save__note', '');
    saveNote.hidden = true;
    saveRow.appendChild(saveNote);
    var revertBtn = btn('yt-portal-save__revert', 'Discard changes', function () {
      resetStackFromOverview();
      renderPortal(); // full redraw — the header name needs resetting too
    });
    revertBtn.hidden = true;
    saveRow.appendChild(revertBtn);
    stackCard.appendChild(saveRow);

    var builderLink = el('a', 'yt-portal-stack__builder-link', 'Want a bigger rework? Open the full builder →');
    builderLink.href = BUILD_URL + '?source=portal&subscription=' + encodeURIComponent(state.subId);
    stackCard.appendChild(builderLink);
    colMain.appendChild(stackCard);

    function redrawStack() {
      while (list.firstChild) list.removeChild(list.firstChild);
      // The base: one line, its own price, a way to change it and a way to
      // take it out — like any ingredient, but for the whole recipe at once.
      if (state.base) {
        var brow = el('div', 'yt-portal-ing yt-portal-ing--base');
        var bmain = el('div', 'yt-portal-ing__main');
        var bname = el('div', 'yt-portal-ing__base');
        bname.appendChild(el('span', 'yt-portal-ing__name', state.base.name));
        bname.appendChild(el('span', 'yt-portal-ing__sub',
          'Base · ' + (state.base.n_ingredients ? state.base.n_ingredients + ' ingredients · ' : '') +
          '£' + (Number(state.base.sub_price_gbp) || 0).toFixed(2) + '/mo'));
        bmain.appendChild(bname);
        bmain.appendChild(btn('yt-portal-ing__change', 'Change', openBaseModal));
        brow.appendChild(bmain);
        var bremove = btn('yt-portal-ing__remove', '✕', function () {
          if (state.stack.length === 0) {
            // Nothing else in the tin: a blend needs a base or an ingredient.
            openBaseModal();
            return;
          }
          track('portal_base_removed', { base: state.base.slug });
          state.base = null;
          state.dirty = true;
          redrawStack();
        });
        bremove.setAttribute('aria-label', 'Remove the ' + state.base.name + ' base');
        brow.appendChild(bremove);
        list.appendChild(brow);
      } else {
        var addBaseRow = el('div', 'yt-portal-ing yt-portal-ing--addbase');
        addBaseRow.appendChild(btn('yt-account__btn yt-account__btn--secondary', '+ Add a base', openBaseModal));
        addBaseRow.appendChild(el('span', 'yt-portal-ing__sub', 'One of our ready-made blends, at its own price, with your extras on top.'));
        list.appendChild(addBaseRow);
      }
      if (state.base && state.stack.length > 0) list.appendChild(el('p', 'yt-portal-stack__label', 'On top of the base'));
      state.stack.forEach(function (s, idx) {
        var row = el('div', 'yt-portal-ing');
        var main = el('div', 'yt-portal-ing__main');
        main.appendChild(el('span', 'yt-portal-ing__name', s.name));
        var cat = state.catalogueById ? state.catalogueById.get(s.ingredient_id) : null;

        var dose = el('div', 'yt-portal-ing__dose');
        var minus = btn('yt-portal-ing__step', '−', function () { stepDose(idx, -1); });
        var doseVal = el('span', 'yt-portal-ing__dose-val', mgToDisplay(s.amount_mg, s.unit));
        var plus = btn('yt-portal-ing__step', '+', function () { stepDose(idx, +1); });
        if (cat) {
          minus.disabled = s.amount_mg <= Number(cat.min_dose_mg);
          plus.disabled = s.amount_mg >= Number(cat.max_dose_mg);
        } else {
          // Not in the live catalogue yet (still loading, or a retired
          // ingredient): dose locked, removal still allowed.
          minus.disabled = true;
          plus.disabled = true;
        }
        dose.appendChild(minus);
        dose.appendChild(doseVal);
        dose.appendChild(plus);
        main.appendChild(dose);
        var issue = doseIssue(s, cat);
        if (issue) {
          row.classList.add('is-offmenu');
          var warn = el('div', 'yt-portal-ing__warn');
          warn.appendChild(el('span', 'yt-portal-ing__warn-text', issue.text + '.'));
          warn.appendChild(btn('yt-portal-ing__fix', 'Use ' + mgToDisplay(issue.fix, s.unit), function () {
            s.amount_mg = issue.fix;
            state.dirty = true;
            track('portal_dose_snapped', { ingredient: s.name, to_mg: issue.fix });
            redrawStack();
          }));
          main.appendChild(warn);
        }
        row.appendChild(main);

        var remove = btn('yt-portal-ing__remove', '✕', function () {
          if (state.stack.length <= 1 && !state.base) return;
          state.stack.splice(idx, 1);
          state.dirty = true;
          redrawStack();
          track('portal_ingredient_removed', { ingredient: s.name });
        });
        // The last ingredient can go when a base is under it; a scratch blend keeps one.
        remove.disabled = state.stack.length <= 1 && !state.base;
        remove.setAttribute('aria-label', 'Remove ' + s.name);
        row.appendChild(remove);
        list.appendChild(row);
      });

      // Always show a price: the live preview once the catalogue is in,
      // the stored monthly price before that (or if a row can't be priced).
      var preview = computeMonthlyPrice();
      if (state.dirty && preview != null) {
        priceEl.textContent = 'New price: £' + preview.toFixed(2) + '/month';
      } else {
        var current = fmtMoney(o.subscription.price) || fmtMoney(o.formula.monthly_sub_price) ||
          (preview != null ? '£' + preview.toFixed(2) : null);
        priceEl.textContent = current ? 'Monthly price: ' + current + '/month' : '';
      }
      var offMenu = doseIssues();
      saveBtn.disabled = !state.dirty || state.busy || (state.stack.length < 1 && !state.base) || offMenu.length > 0;
      saveNote.hidden = offMenu.length === 0;
      saveNote.textContent = offMenu.length === 0 ? '' :
        (offMenu.length === 1
          ? 'One dose above is not a size we make — use the size offered, or adjust it, to update your subscription.'
          : offMenu.length + ' doses above are not sizes we make — use the sizes offered, or adjust them, to update your subscription.');
      revertBtn.hidden = !state.dirty;
      drawNutrition();
    }

    // What is actually in the tin, per daily scoop — the same table the
    // PDP prints, under the editable lines (Lucas, 24 Sep 2026). From the
    // overview's snapshot; it does not re-total while editing.
    function drawNutrition() {
      var rows = (o.formula && o.formula.nutrition) || [];
      var existing = stackCard.querySelector('[data-yt-portal-nutrition]');
      if (existing) existing.remove();
      if (!rows.length || state.dirty) return;
      var det = document.createElement('details');
      det.className = 'yt-portal-nutrition';
      det.setAttribute('data-yt-portal-nutrition', '');
      var sum = document.createElement('summary');
      sum.textContent = 'Nutritional information — what’s in your daily scoop';
      det.appendChild(sum);
      var table = document.createElement('table');
      table.className = 'yt-portal-nutrition__t';
      var thead = document.createElement('thead');
      thead.innerHTML = '<tr><th scope="col">Ingredient</th><th scope="col">Per daily dose</th><th scope="col">%NRV</th></tr>';
      table.appendChild(thead);
      var tbody = document.createElement('tbody');
      rows.forEach(function (r) {
        var tr = document.createElement('tr');
        var td1 = document.createElement('td'); td1.textContent = r.panel_name || r.name || '';
        var td2 = document.createElement('td'); td2.textContent = r.dose_display || '';
        var td3 = document.createElement('td'); td3.textContent = r.nrv_pct != null ? r.nrv_pct + '%' : '†';
        tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3);
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      det.appendChild(table);
      det.appendChild(el('p', 'yt-portal-nutrition__note', '† No nutrient reference value set. Save your changes to see the updated table.'));
      stackCard.insertBefore(det, footer);
    }

    // ── base chooser ─────────────────────────────────────────────────────
    function openBaseModal() {
      loadBases().then(function (bases) {
        var overlay = el('div', 'yt-portal-modal');
        var box = el('div', 'yt-portal-modal__box');
        var mhead = el('div', 'yt-portal-modal__head');
        mhead.appendChild(el('h3', 'yt-portal-modal__title', state.base ? 'Change the base' : 'Add a base'));
        mhead.appendChild(btn('yt-portal-modal__close', '✕', function () { overlay.remove(); }));
        box.appendChild(mhead);
        box.appendChild(el('p', 'yt-portal-modal__lede', 'A base is one of our ready-made blends, sold as one line at its own price. Your extras stay on top of whichever you pick.'));
        var results = el('div', 'yt-portal-modal__results');
        box.appendChild(results);
        overlay.appendChild(box);
        overlay.addEventListener('click', function (e) { if (e.target === overlay) overlay.remove(); });
        document.body.appendChild(overlay);
        if (!bases.length) {
          results.appendChild(el('p', 'yt-portal-modal__empty', 'No bases are available just now.'));
          return;
        }
        bases.forEach(function (b) {
          var current = state.base && state.base.slug === b.slug;
          var row = btn('yt-portal-modal__item' + (current ? ' is-current' : ''), '', function () {
            if (current) { overlay.remove(); return; }
            state.base = {
              slug: b.slug,
              name: b.name,
              sub_price_gbp: b.prices && b.prices.sub != null ? Number(b.prices.sub) : null,
              n_ingredients: b.n_ingredients || (b.ingredients ? b.ingredients.length : null),
            };
            // An extra the new base already contains would be stacked on top of
            // it twice; it comes off the extras rather than doubling quietly.
            var inBase = {};
            (b.ingredients || []).forEach(function (i) { inBase[i.slug] = true; });
            state.stack = state.stack.filter(function (s) {
              var cat = state.catalogueById ? state.catalogueById.get(s.ingredient_id) : null;
              return !(cat && inBase[cat.slug]);
            });
            state.dirty = true;
            overlay.remove();
            redrawStack();
            track('portal_base_changed', { base: b.slug });
          });
          row.appendChild(el('span', 'yt-portal-modal__item-name', b.name + (current ? ' · current' : '')));
          row.appendChild(el('span', 'yt-portal-modal__item-meta',
            (b.n_ingredients || (b.ingredients ? b.ingredients.length : '')) + ' ingredients · £' +
            (b.prices && b.prices.sub != null ? Number(b.prices.sub).toFixed(2) : '—') + '/mo'));
          results.appendChild(row);
        });
      }).catch(function () {
        alert('We could not load the bases just now. Please try again.');
      });
    }

    function stepDose(idx, dir) {
      var s = state.stack[idx];
      var cat = state.catalogueById ? state.catalogueById.get(s.ingredient_id) : null;
      if (!cat) return;
      var min = Number(cat.min_dose_mg);
      var max = Number(cat.max_dose_mg);
      var step = Number(cat.dose_step_mg) || 50;
      var next = s.amount_mg + dir * step;
      // Land ON the menu: from an off-menu dose a plain step stays off it
      // (500 − 50 = 450 against a 100-step menu), so snap to the grid first.
      next = min + Math.round((next - min) / step) * step;
      next = Math.min(max, Math.max(min, next));
      next = Math.round(next * 1e6) / 1e6;
      if (next !== s.amount_mg) {
        s.amount_mg = next;
        state.dirty = true;
        redrawStack();
      }
    }

    // A dose the menu cannot make: above the ceiling, below the floor, or
    // between two sizes. Legacy blends carry such doses from before the
    // component catalogue (Robin's 500 mg magnesium glycinate against a
    // 400 mg menu) and the row looked perfectly normal — until the save was
    // refused. Name it on the row and offer the nearest size. The server
    // still has the last word (its menu can differ from min/max/step); its
    // message now reaches the banner via apiMessage.
    function doseIssue(s, cat) {
      if (!cat) return null;
      var min = Number(cat.min_dose_mg);
      var max = Number(cat.max_dose_mg);
      var step = Number(cat.dose_step_mg) || 0;
      var mg = Number(s.amount_mg);
      if (!(min > 0) || !(max >= min) || !(mg > 0)) return null;
      if (mg > max) return { text: 'We go up to ' + mgToDisplay(max, s.unit) + ' a day', fix: max };
      if (mg < min) return { text: 'We start at ' + mgToDisplay(min, s.unit) + ' a day', fix: min };
      if (step > 0) {
        var snapped = Math.min(max, min + Math.round((mg - min) / step) * step);
        snapped = Math.round(snapped * 1e6) / 1e6;
        if (Math.abs(snapped - mg) > step * 0.001) {
          return { text: mgToDisplay(mg, s.unit) + ' is between the sizes we make', fix: snapped };
        }
      }
      return null;
    }
    function doseIssues() {
      if (!state.catalogueById) return [];
      return state.stack.filter(function (s) { return doseIssue(s, state.catalogueById.get(s.ingredient_id)); });
    }

    // ── add-ingredient modal ─────────────────────────────────────────────
    // Full catalogue, A→Z; typing switches to the ranked typo-tolerant
    // search (same engine as BYO), best match first.
    function openAddModal() {
      loadCatalogue().then(function () {
        var overlay = el('div', 'yt-portal-modal');
        var box = el('div', 'yt-portal-modal__box');
        var mhead = el('div', 'yt-portal-modal__head');
        mhead.appendChild(el('h3', 'yt-portal-modal__title', 'Add an ingredient'));
        mhead.appendChild(btn('yt-portal-modal__close', '✕', function () { overlay.remove(); }));
        box.appendChild(mhead);
        var search = el('input', 'yt-portal-modal__search');
        search.type = 'search';
        search.placeholder = 'Search ingredients…';
        box.appendChild(search);
        var results = el('div', 'yt-portal-modal__results');
        box.appendChild(results);
        overlay.appendChild(box);
        overlay.addEventListener('click', function (e) { if (e.target === overlay) overlay.remove(); });
        document.body.appendChild(overlay);
        search.focus();

        function draw(q) {
          while (results.firstChild) results.removeChild(results.firstChild);
          var inStack = {};
          state.stack.forEach(function (s) { inStack[s.ingredient_id] = true; });
          var items;
          if (searchNormalise(q)) {
            var scores = searchScores(state.searchIndex, q);
            items = state.catalogue.filter(function (i) { return scores.has(i.id); });
            items.sort(function (a, b) {
              return (scores.get(b.id) - scores.get(a.id)) || String(a.name).localeCompare(String(b.name));
            });
          } else {
            items = state.catalogue; // already A→Z
          }
          var shown = 0;
          items.forEach(function (ing) {
            if (inStack[ing.id]) return;
            shown += 1;
            var row = btn('yt-portal-modal__item', '', function () {
              state.stack.unshift({
                ingredient_id: ing.id,
                amount_mg: Number(ing.default_dose_mg),
                name: ing.name,
                unit: ing.unit || 'mg',
              });
              state.dirty = true;
              overlay.remove();
              redrawStack();
              track('portal_ingredient_added', { ingredient: ing.name });
            });
            row.appendChild(el('span', 'yt-portal-modal__item-name', ing.name));
            row.appendChild(el('span', 'yt-portal-modal__item-dose', mgToDisplay(ing.default_dose_mg, ing.unit)));
            results.appendChild(row);
          });
          if (!shown) results.appendChild(el('p', 'yt-portal-modal__empty', 'No matching ingredients.'));
        }
        search.addEventListener('input', function () { draw(search.value); });
        draw('');
      }).catch(function () {
        banner('err', 'Could not load the ingredient list. Please try again.');
      });
    }

    // ── save: mint variant → swap subscription ───────────────────────────
    function saveStack() {
      if (state.busy || !state.dirty) return;
      state.busy = true;
      saveBtn.disabled = true;
      saveBtn.textContent = 'Updating…';
      api('/formula/submit', {
        method: 'POST',
        timeout: 45000,
        headers: { 'Idempotency-Key': newIdempotencyKey() },
        body: {
          stack: {
            // Not the customer's to choose: the server prints "My Blend"
            // (or the base's tin name) whatever is sent here.
            stack_name: o.formula.stack_name || 'My Blend',
            primary_goals: [],
            // The ADD-ONS only. The base goes as base_slug and is sold as one
            // line; sending its ingredients flat is what minted a baseless
            // duplicate and doubled a customer's price (RS Britten, 10 Sep).
            ingredients: state.stack.map(function (s) {
              return { ingredient_id: s.ingredient_id, amount_mg: s.amount_mg };
            }),
          },
          base_slug: state.base ? state.base.slug : undefined,
          formula_id_prefill: o.formula.id || undefined,
          edited_after_ai: true,
        },
      }).then(function (submit) {
        // BUNDLE WORLD: nothing mints (variant_id null) — the group-diff
        // route replaces the blend's component subscriptions in place.
        // state.subId is the GROUP KEY for bundle blends; the route
        // resolves it. Same shaped result feeds the disclosure below.
        var isBundle = Boolean(submit.bundle) || !submit.variant_id;
        if (isBundle) {
          if (!submit.formula_id) {
            throw new Error(apiMessage(submit, 'We could not save your blend. Please try again.'));
          }
          return api('/account/subscriptions/' + encodeURIComponent(state.subId) + '/update-bundle', {
            method: 'POST',
            timeout: 45000,
            body: { formula_id: submit.formula_id },
          }).then(function (upd) {
            if (!upd.ok) throw new Error(apiMessage(upd, 'We could not update your subscription. Please try again.'));
            // A legacy line becomes a component GROUP on its first edit
            // (the route's legacy-migrate): the old id is cancelled and the
            // blend now answers to the group key. Adopt it here, or the next
            // save — and every button on this page — would ask after a
            // cancelled subscription (Robin Maximova, 28 Sep 2026).
            if (upd.group_key && upd.group_key !== state.subId) {
              var oldId = state.subId;
              state.subId = upd.group_key;
              (state.subs || []).forEach(function (sub) { if (sub.id === oldId) sub.id = upd.group_key; });
            }
            var monthly = submit.prices && submit.prices.monthly_sub != null
              ? Number(submit.prices.monthly_sub).toFixed(2) : null;
            return { ok: true, old: {}, 'new': { price: monthly }, next_charge_at: null };
          });
        }
        var variantId = String(submit.variant_id || '').replace(/\D/g, '');
        if (!variantId) throw new Error(apiMessage(submit, 'We could not save your blend. Please try again.'));
        return api('/account/subscriptions/' + encodeURIComponent(state.subId) + '/swap', {
          method: 'POST',
          timeout: 45000,
          body: { shopify_variant_id: variantId, formula_id: submit.formula_id || undefined },
        });
      }).then(function (swap) {
        if (!swap.ok) throw new Error(apiMessage(swap, 'We could not update your subscription. Please try again.'));
        var newPrice = swap['new'] && swap['new'].price;
        var oldPrice = swap.old && swap.old.price;
        var when = swap.next_charge_at ? fmtDate(swap.next_charge_at) : null;
        var msg = 'Subscription updated.';
        // UK CRA §25 material-change disclosure — same wording family as the
        // BYO portal-mode flow.
        if (newPrice) {
          msg = 'Subscription updated — from your next delivery' + (when ? ' on ' + when : '') +
            ", you'll pay £" + newPrice + '/month' +
            (oldPrice && oldPrice !== newPrice ? ' (was £' + oldPrice + ')' : '') + '.';
        }
        track('portal_stack_updated', { ingredients: state.stack.length });
        state.dirty = false;
        state.busy = false;
        return refresh().then(function () { banner('ok', msg); });
      }).catch(function (err) {
        state.busy = false;
        saveBtn.disabled = false;
        saveBtn.textContent = 'UPDATE MY SUBSCRIPTION';
        banner('err', (err && err.message) || 'Something went wrong. Please try again.');
      });
    }

    // Rows render immediately from the overview payload; the catalogue loads
    // in the background to activate steppers + the live price preview.
    redrawStack();
    loadCatalogue().then(redrawStack).catch(function () { /* rows still usable */ });

    // ── delivery card ────────────────────────────────────────────────────
    var delivery = card('Next delivery');
    delivery.appendChild(el('p', 'yt-portal-delivery__date',
      o.subscription.next_charge_at
        ? 'Arriving ' + arrivalRange(o.subscription.next_charge_at)
        : 'No delivery scheduled'));
    if (o.subscription.next_charge_at) {
      delivery.appendChild(el('p', 'yt-portal-delivery__billed', 'Billed ' + fmtDate(o.subscription.next_charge_at)));
    }
    var dActions = el('div', 'yt-account-card__actions');
    // "Place order now" (founder 2026-08-10 — REPLACES "Skip this delivery"):
    // moves the next charge to TODAY; Recharge bills within the hour and the
    // following delivery auto-schedules one full cycle from today. Branded
    // in-page confirm (founder, same day: no browser popups). Black button.
    dActions.appendChild(btn('yt-account__btn yt-account__btn--black', 'Place order now', function () {
      var p = popup('Place your next order now?');
      p.box.appendChild(el('p', 'yt-portal-pop__text',
        'Your card will be charged today and your blend ships in the next few days — arriving within 5–7 days. Your following delivery moves one full cycle from today.'));
      // Cancel LEFT, confirm RIGHT (founder 2026-08-10) — same convention as
      // the keep/cancel pair in the cancel-subscription popup.
      var acts = el('div', 'yt-portal-pop__actions');
      acts.appendChild(btn('yt-account__btn yt-account__btn--secondary', 'Cancel', function () {
        p.overlay.remove();
      }));
      acts.appendChild(btn('yt-account__btn yt-account__btn--black', 'Place order now', function () {
        p.overlay.remove();
        showLoading('Placing your order…');
        api('/account/subscriptions/' + encodeURIComponent(state.subId) + '/order-now', { method: 'POST', timeout: 45000 })
          .then(function (r) {
            if (!r.ok) throw new Error(r.message || 'Could not place the order.');
            track('portal_order_now', {});
            return refresh().then(function () {
              banner('ok', 'Order placed — it will be billed in the next few hours and arrive within 5–7 days. Your following delivery moves one full cycle from today.');
            });
          })
          .catch(function (err) {
            refresh().then(function () { banner('err', (err && err.message) || 'Could not place the order. Please try again.'); });
          });
      }));
      p.box.appendChild(acts);
    }));
    var dRow = el('div', 'yt-portal-delivery__row');
    dRow.appendChild(btn('yt-account__btn yt-account__btn--secondary', 'Delay delivery', function () {
      var p = popup('Delay your delivery');
      p.box.appendChild(el('p', 'yt-portal-pop__text', 'Push your next delivery back — your blend and price stay exactly the same.'));
      appendDelayOptions(p.box, function () { p.overlay.remove(); });
    }));
    dRow.appendChild(btn('yt-account__btn yt-account__btn--secondary', 'Pick delivery date', function () {
      var p = popup('Pick a delivery date');
      p.box.appendChild(el('p', 'yt-portal-pop__text', 'Choose the day you’d like your blend to arrive — we’ll time the order so it lands around then.'));
      var input = el('input', 'yt-portal-datepick__input');
      input.type = 'date';
      // Founder 2026-08-11: arrivals under 5 days out aren’t pickable — a
      // made-to-order tin can’t beat the 5–7 day door-to-door time.
      var minArrival = isoOf(addDays(new Date(), DELIVERY_MIN_DAYS));
      input.min = minArrival;
      // Default to the current schedule’s estimated arrival (charge + backoff).
      var currentArrival = o.subscription.next_charge_at
        ? isoOf(addDays(dateFromIso(o.subscription.next_charge_at), ARRIVAL_BACKOFF_DAYS))
        : null;
      input.value = currentArrival && currentArrival >= minArrival ? currentArrival : minArrival;
      input.setAttribute('aria-label', 'Delivery arrival date');
      p.box.appendChild(input);
      var note = el('p', 'yt-portal-pop__note', '');
      function syncBilledNote() {
        var charge = input.value ? chargeForArrival(input.value) : null;
        note.textContent = charge ? 'Billed ' + fmtDate(charge) + '.' : '';
      }
      syncBilledNote();
      input.addEventListener('change', syncBilledNote);
      input.addEventListener('input', syncBilledNote);
      p.box.appendChild(note);
      var acts = el('div', 'yt-portal-pop__actions');
      acts.appendChild(btn('yt-account__btn yt-account__btn--primary', 'Set delivery date', function () {
        if (!input.value || input.value < minArrival) return;
        p.overlay.remove();
        postNextDate(chargeForArrival(input.value), 'moved');
      }));
      p.box.appendChild(acts);
    }));
    dActions.appendChild(dRow);
    delivery.appendChild(dActions);
    colSide.appendChild(delivery);

    // (No address card here: delivery addresses are managed on the account
    // shell's Profile page — founder 2026-07-29, same reasoning as payment.)

    // (No payment card here: card management lives on the account shell's
    // Profile page — founder 2026-07-29, after the Recharge-hosted link
    // proved flaky.)

    // ── pause-or-cancel card ─────────────────────────────────────────────
    // Visible card with both ways to stop: Pause (resumable — Recharge cancel
    // with the pause sentinel) and Cancel (soft retention popup offering the
    // delay options first, then the real cancel with an optional reason).
    /** The final cancel step (reason + confirm), appended into popup `p`. */
    function cancelReasonStep(p) {
      var confirm = el('div');
      confirm.appendChild(el('p', 'yt-portal-pop__text',
        "We're sorry to see you go. Mind telling us why? (Optional.)"));
      var reason = el('textarea', 'yt-portal-pop__reason');
      reason.rows = 3;
      reason.placeholder = 'Your reason (optional)';
      confirm.appendChild(reason);
      var acts = el('div', 'yt-portal-pop__actions');
      acts.appendChild(btn('yt-account__btn yt-account__btn--secondary', 'Keep my subscription', function () {
        p.overlay.remove();
      }));
      acts.appendChild(btn('yt-account__btn yt-portal-pop__danger-btn', 'Cancel subscription', function () {
        p.overlay.remove();
        showLoading('Cancelling…');
        api('/account/subscriptions/' + encodeURIComponent(state.subId) + '/cancel', {
          method: 'POST',
          body: { reason: reason.value.trim() || 'No reason given' },
        }).then(function (r) {
          if (!r.ok) throw new Error(r.message || 'Could not cancel just now.');
          track('portal_cancelled', {});
          return refresh().then(function () { banner('ok', 'Your subscription is cancelled. You can restart any time.'); });
        }).catch(function (err) {
          refresh().then(function () { banner('err', (err && err.message) || 'Could not cancel just now.'); });
        });
      }));
      confirm.appendChild(acts);
      p.box.appendChild(confirm);
    }

    var stopCard = card('Pause or cancel');
    stopCard.appendChild(el('p', 'yt-account__state-text',
      'Pause stops deliveries and payments until you resume. Either way your blend stays saved.'));
    var stopActs = el('div', 'yt-portal-delivery__row');
    stopActs.appendChild(btn('yt-account__btn yt-account__btn--secondary', 'Pause', function () {
      var p = popup('Pause your subscription?');
      p.box.appendChild(el('p', 'yt-portal-pop__text',
        'Deliveries and payments stop until you resume — your blend and price stay exactly as they are.'));
      var acts = el('div', 'yt-portal-pop__actions');
      acts.appendChild(btn('yt-account__btn yt-account__btn--secondary', 'Keep it running', function () {
        p.overlay.remove();
      }));
      acts.appendChild(btn('yt-account__btn yt-account__btn--primary', 'Pause subscription', function () {
        p.overlay.remove();
        showLoading('Pausing…');
        api('/account/subscriptions/' + encodeURIComponent(state.subId) + '/pause', { method: 'POST' })
          .then(function (r) {
            if (!r.ok) throw new Error(r.message || 'Could not pause just now.');
            track('portal_paused', {});
            return refresh().then(function () { banner('ok', 'Subscription paused — resume any time from this page.'); });
          })
          .catch(function (err) {
            refresh().then(function () { banner('err', (err && err.message) || 'Could not pause just now.'); });
          });
      }));
      p.box.appendChild(acts);
    }));
    stopActs.appendChild(btn('yt-account__btn yt-portal-cancel__btn', 'Cancel', function () {
      var p = popup('Before you go…');
      var offer = el('div');
      offer.appendChild(el('p', 'yt-portal-pop__text',
        'If the timing is off, you can push your next delivery back instead — your blend and price stay locked in.'));
      appendDelayOptions(offer, function () { p.overlay.remove(); });
      offer.appendChild(btn('yt-portal-pop__danger-link', 'No thanks — continue to cancel', function () {
        offer.remove();
        cancelReasonStep(p);
      }));
      p.box.appendChild(offer);
    }));
    stopCard.appendChild(stopActs);
    colSide.appendChild(stopCard);

    root.appendChild(grid);
  }

  function resetStackFromOverview() {
    var f = state.overview.formula;
    var b = f.base || null;
    // With a base, the rows it contributes (from_base) are not editable lines
    // — the base is. Only the extras become rows.
    var rows = (f.ingredients || []).filter(function (i) { return i.ingredient_id && !(b && i.from_base); });
    state.base = b ? {
      slug: b.slug,
      name: b.name,
      sub_price_gbp: b.sub_price_gbp != null ? Number(b.sub_price_gbp) : null,
      n_ingredients: b.n_ingredients || (f.ingredients || []).filter(function (i) { return i.from_base; }).length || null,
    } : null;
    state.stack = rows.map(function (i) {
      return { ingredient_id: i.ingredient_id, amount_mg: Number(i.amount_mg) || 0, name: i.name, unit: i.unit || 'mg' };
    });
    state.stackName = f.stack_name || null;
    state.dirty = false;
  }

  // --- boot ----------------------------------------------------------------

  function refresh() {
    // Never rejects: any load OR render failure lands on the retry state
    // instead of leaving whatever was on screen (usually a spinner) stuck.
    // Action handlers chain banners after this; a banner over the retry
    // state is harmless and still truthful about what the action did.
    // 45s timeout: the overview chains several Recharge reads server-side
    // (up to ~30s when Recharge is slow, e.g. right after an activation) —
    // the default 25s abort made a SUCCESSFUL resume land on Retry.
    return api('/account/subscriptions/' + encodeURIComponent(state.subId) + '/overview', { timeout: 45000 })
      .then(function (o) {
        if (!o.ok) throw new Error(o.message || 'load_failed');
        state.overview = o;
        resetStackFromOverview();
        renderPortal();
      })
      .catch(function () {
        showState('Something went wrong', 'We could not reload your subscription. Please try again.', [
          btn('yt-account__btn yt-account__btn--primary', 'Retry', boot),
        ]);
      });
  }

  function boot() {
    showLoading();
    api('/account/subscriptions').then(function (d) {
      if (d.__status === 401) {
        var signin = el('a', 'yt-account__btn yt-account__btn--primary', 'Sign in');
        signin.href = '/account';
        showState(
          'Sign in to manage your subscription',
          'View your next delivery, change your stack, or pause anytime.',
          [signin]
        );
        return;
      }
      var subs = (d.subscriptions || []).filter(function (s) {
        return s.status === 'active' || s.status === 'paused';
      });
      if (!subs.length) {
        // No live sub, but an old cancelled one can be restarted in place —
        // show it rather than pretending nothing ever existed.
        subs = (d.subscriptions || []).filter(function (s) { return s.status === 'cancelled'; });
      }
      if (!subs.length) {
        var build = el('a', 'yt-account__btn yt-account__btn--primary', 'Build your blend');
        build.href = BUILD_URL;
        showState(
          'No active subscription yet',
          'Build your formula and subscribe to get it fresh-made and delivered on your schedule.',
          [build]
        );
        return;
      }
      state.subs = subs;
      if (subs.length > 1) {
        renderPicker();
        return;
      }
      state.subId = subs[0].id;
      return refresh();
    }).catch(function () {
      showState('Something went wrong', 'We could not load your subscription. Please try again.', [
        btn('yt-account__btn yt-account__btn--primary', 'Retry', boot),
      ]);
    });
  }

  boot();
})();
